import {
  always,
  applySpec,
  concat,
  converge,
  defaultTo,
  evolve,
  filter,
  ifElse,
  map,
  pick,
  pipe,
  prop,
  propOr,
  reject,
} from "ramda";
import { useContext } from "react";
import { useMyMutation, useMyQuery } from "../Authorization/AuthProvider";
import { I18nContext } from "../I18n/I18nProvider";
import { UTC_DATE_FORMAT } from "../I18n/utils/date";
import { SESSION_FIELDS } from "./steps/constants";
import { useGamification } from "../Gamification/GamificationProvider";

export const useUserSessionQuery = (rest = {}) => {
  const query = useMyQuery({
    queryKey: ["user-sessions"],
    fetchDef: { url: `/api/latest/user-sessions` },
    // be careful, following params affect form reinit
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    ...rest,
  });
  return query;
};

// FIRST_ACTIONS + REVIEW_ACTIONS with adjust
export const useUserSessionMutation = ({ withPrevActionSteps } = {}) => {
  const { i18n } = useContext(I18nContext);
  const { handleSessionCompleted } = useGamification();
  const formatFP = (date) => i18n.formatLocalMaybe(date, UTC_DATE_FORMAT);
  const mutation = useMyMutation({
    fetchDef: {
      method: "POST",
      url: "/api/latest/user-sessions",
      from: applySpec({
        areaOfDevelopment: prop(SESSION_FIELDS.AREA_OF_DEVELOPMENT),
        longTermGoal: prop(SESSION_FIELDS.LONG_TERM_GOAL),
        motivation: prop(SESSION_FIELDS.MOTIVATION),
        actionSteps: converge(concat, [
          pipe(
            withPrevActionSteps
              ? propOr([], SESSION_FIELDS.PREV_ACTION_STEPS)
              : always([]),
            reject(prop("checked")),
          ),
          pipe(
            prop(SESSION_FIELDS.ACTION_STEPS),
            defaultTo([]),
            map(pick(["label", "date"])),
            map(evolve({ date: formatFP })),
          ),
        ]),
      }),
    },
    invalidate: [
      { queryKey: ["user-sessions"] },
      { queryKey: ["user-info"] },
      { queryKey: ["user-insight"] },
      { queryKey: ["user-badges"] },
    ],
    onSuccess: () => handleSessionCompleted(),
  });
  return mutation;
};

// on REFLECT with adjust + REVIEW_ACTIONS without adjust
export const useUserReflectionMutation = () => {
  const { i18n } = useContext(I18nContext);
  const { handleSessionCompleted } = useGamification();
  const formatFP = (date) => i18n.formatLocalMaybe(date, UTC_DATE_FORMAT);
  const mutation = useMyMutation({
    fetchDef: {
      method: "POST",
      url: "/api/latest/user-sessions-reflection",
      from: applySpec({
        reflection: prop(SESSION_FIELDS.REFLECTION),
        newActionSteps:
          // newActionSteps are adding, not setting
          pipe(
            prop(SESSION_FIELDS.ACTION_STEPS),
            defaultTo([]),
            map(pick(["label", "date"])),
            map(evolve({ date: formatFP })),
          ),
        checked: pipe(
          prop(SESSION_FIELDS.PREV_ACTION_STEPS),
          defaultTo([]),
          filter(prop("checked")),
          map(prop("id")),
        ),
      }),
    },
    invalidate: [
      { queryKey: ["user-sessions"] },
      { queryKey: ["user-info"] },
      { queryKey: ["user-insight"] },
      { queryKey: ["user-badges"] },
    ],
    onSuccess: () => handleSessionCompleted(),
  });
  return mutation;
};
